/**
 * Sticker / label pricing calculation module.
 * Fetches config from /api/configurators/stickers with a hardcoded fallback.
 */

const FALLBACK_CONFIG = {
  formats: [
    { id: "die-cut", label: "Die-Cut Stickers", base_rate_per_sqcm: 0.016, default_quantity: 100 },
    { id: "kiss-cut", label: "Kiss-Cut Stickers", base_rate_per_sqcm: 0.013, default_quantity: 100 },
    { id: "sticker-sheets", label: "Sticker Sheets", base_rate_per_sqcm: 0.012, default_quantity: 25 },
    { id: "product-labels", label: "Product Labels", base_rate_per_sqcm: 0.008, default_quantity: 250 },
    { id: "window-decals", label: "Window Decals", base_rate_per_sqcm: 0.005, default_quantity: 1 },
  ],
  materials: [
    { id: "white-vinyl", label: "White Vinyl", surcharge_pct: 0 },
    { id: "clear-vinyl", label: "Clear Vinyl", surcharge_pct: 15 },
    { id: "kraft", label: "Kraft Paper", surcharge_pct: 10 },
    { id: "adhesive-vinyl", label: "Adhesive Vinyl", surcharge_pct: 0 },
    { id: "static-cling", label: "Static Cling", surcharge_pct: 20 },
    { id: "frosted-vinyl", label: "Frosted Vinyl", surcharge_pct: 18 },
  ],
  finishes: [
    { id: "matte", label: "Matte", surcharge_pct: 0 },
    { id: "gloss", label: "Gloss", surcharge_pct: 0 },
  ],
  lamination_surcharge_pct: 15,
  minimum_order_gbp: 15.0,
  quantity_tiers: [
    { min: 1, max: 24, multiplier: 2.0, label: "1\u201324" },
    { min: 25, max: 49, multiplier: 1.6, label: "25\u201349" },
    { min: 50, max: 99, multiplier: 1.3, label: "50\u201399" },
    { min: 100, max: 249, multiplier: 1.0, label: "100\u2013249" },
    { min: 250, max: 499, multiplier: 0.85, label: "250\u2013499" },
    { min: 500, max: 999, multiplier: 0.72, label: "500\u2013999" },
    { min: 1000, max: 99999, multiplier: 0.6, label: "1,000+" },
  ],
};

let _cachedConfig = null;

export async function fetchStickerConfig() {
  if (_cachedConfig) return _cachedConfig;
  try {
    const base = "";
    const res = await fetch(`${base}/api/configurators/stickers`);
    if (!res.ok) throw new Error(res.statusText);
    _cachedConfig = await res.json();
    return _cachedConfig;
  } catch (err) {
    console.error("[stickerPricing] fetch config failed, using fallback:", err);
    _cachedConfig = FALLBACK_CONFIG;
    return _cachedConfig;
  }
}

export function getFallbackStickerConfig() {
  return FALLBACK_CONFIG;
}

/**
 * @param {object} opts
 * @param {string} opts.formatId    - "die-cut", "kiss-cut", etc.
 * @param {number} opts.areaSqcm    - area in cm²
 * @param {string} opts.materialId  - "white-vinyl", "clear-vinyl", etc.
 * @param {boolean} opts.lamination
 * @param {number} opts.quantity
 * @param {object} config
 */
export function calculateStickerPrice(
  { formatId, areaSqcm, materialId = "white-vinyl", lamination = false, quantity = 1 },
  config = FALLBACK_CONFIG
) {
  const format = config.formats.find((f) => f.id === formatId) || config.formats[0];
  const material = config.materials.find((m) => m.id === materialId) || config.materials[0];

  const baseRate = format.base_rate_per_sqcm;
  const materialMult = 1 + (material.surcharge_pct || 0) / 100;
  const laminationMult = lamination && format.has_lamination !== false ? 1 + (config.lamination_surcharge_pct || 0) / 100 : 1;

  const qty = Math.max(1, quantity);
  const tier = (config.quantity_tiers || []).find((t) => qty >= t.min && qty <= t.max);
  const qtyMultiplier = tier ? tier.multiplier : 1;

  let unitPrice = areaSqcm * baseRate * materialMult * laminationMult * qtyMultiplier;

  // Enforce minimum order value spread across quantity
  const minPerUnit = (config.minimum_order_gbp || 15) / qty;
  if (unitPrice < minPerUnit) unitPrice = minPerUnit;

  const totalPrice = unitPrice * qty;

  return {
    unitPrice: Math.round(unitPrice * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
    qtyMultiplier,
    qtyLabel: tier?.label || "",
  };
}

export function formatGBP(amount) {
  if (amount < 0.01) return "\u00a30.01";
  return `\u00a3${amount.toFixed(2)}`;
}
